import { useEffect, useState, useCallback, Activity } from 'react';
import { v4 as uuid } from 'uuid';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  SidePanelLoader,
  ButtonSecondary,
  Currency,
  Popup,
  usePrevious,
  PermissionEnum,
  responseErrorsResolver,
  ResponseError,
  Tabs,
  ChangeLogs,
  PermissionDenied,
  PaymentType,
  IconExpand,
  ButtonSize,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useUpdateCustomersExternalAccountStatusMutation,
  useLazyGetCustomersExternalAccountQuery,
  useLazyGetCustomersExternalAccountChangeLogsQuery,
  useLazyGetCustomersExternalAccountTopUpOptionsQuery,
  useUpdateCustomerExternalAccountCurrenciesMutation,
  useDeleteCustomerPaymentAccountsMutation,
  useCreateCustomerPaymentsAccountsMutation,
  useUpdateCustomerPaymentsAccountsMutation,
} from '../store/services/customers';
import {
  Balance,
  CustomerStatus,
  CustomerType,
  ExternalAccountTopUpOptionFormValues,
} from '../types';
import { useLazyGetIndividualAccountBalanceQuery } from '../../individual/store/services/individual';
import ExternalAccountForm from '../../../components/partial/external/item';
import { clearExternalAccountPanel } from '../store/slices/external-accounts';
import ExternalAccountTopUpOptionItem from '../../../components/partial/external/top-op-option';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const ExternalAccount = ({ type }: { type: CustomerType }) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<string[]>(
    [],
  );
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<
    'details' | 'top-up' | 'change-logs'
  >('details');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [isKeel, setIsKeel] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [defaultTopUpOptions, setDefaultTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [topUpOptions, setTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [optionsErrors, setOptionsErrors] = useState<
    {
      id: string;
      currencyId?: string;
      paymentType?: string;
    }[]
  >([]);

  const [searchValue, setSearchValue] = useState('');

  const prevStatus = usePrevious(status);

  const [
    getBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetIndividualAccountBalanceQuery();
  const [updateStatus, { isLoading: requestInProgress }] =
    useUpdateCustomersExternalAccountStatusMutation();
  const [
    getExternalAccount,
    {
      isLoading: externalAccountIsLoading,
      isFetching: externalAccountIsFetching,
    },
  ] = useLazyGetCustomersExternalAccountQuery();
  const [
    getExternalAccountTopUpOptions,
    { isLoading: topUpIsLoading, isFetching: topUpIsFetching },
  ] = useLazyGetCustomersExternalAccountTopUpOptionsQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersExternalAccountChangeLogsQuery();
  const [
    updateCustomerExternalAccountCurrencies,
    {
      isLoading: updateCustomerExternalAccountCurrenciesInProgress,
      isSuccess: updateCustomerExternalAccountCurrenciesIsSuccess,
    },
  ] = useUpdateCustomerExternalAccountCurrenciesMutation();
  const [
    deletePaymentAccounts,
    {
      isLoading: deletePaymentAccountsInProgress,
      isSuccess: deletePaymentAccountsIsSuccess,
    },
  ] = useDeleteCustomerPaymentAccountsMutation();
  const [
    createPaymentAccounts,
    {
      isLoading: createPaymentAccountsInProgress,
      isSuccess: createPaymentAccountsIsSuccess,
    },
  ] = useCreateCustomerPaymentsAccountsMutation();
  const [
    updatePaymentAccounts,
    {
      isLoading: updatePaymentAccountsInProgress,
      isSuccess: updatePaymentAccountsIsSuccess,
    },
  ] = useUpdateCustomerPaymentsAccountsMutation();

  const { search } = useLocation();
  const navigate = useNavigate();

  const { accountId, customerId } = useParams();

  const {
    customersDictionaries: { paymentTypes, activePermissions, casp },
    customersExternalAccounts: {
      externalAccount,
      changeLogs,
      changeLogsPagination,
      externalAccountTopUpOptions,
      pspIntegrations,
    },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { currencies: dictionariesCurrencies, currenciesList } = useAppSelector(
    (state) => state.root,
  );

  const dispatch = useAppDispatch();

  const isDirty =
    Boolean(prevStatus && status !== prevStatus) ||
    !isEqual(topUpOptions, defaultTopUpOptions) ||
    !isEqual(externalAccount?.currenciesIds, selectedCurrencies);

  const organizationStatus =
    type === CustomerType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const deletePaymentAccountsAction = async () => {
    await deletePaymentAccounts(selectedOptionId);

    if (optionsErrors?.find((item) => item.id === selectedOptionId)) {
      setOptionsErrors(
        optionsErrors?.filter((item) => item.id !== selectedOptionId),
      );
    }
  };

  const onSubmit = async () => {
    setGlobalError('');

    setOptionsErrors([]);

    const optionsForCreate: ExternalAccountTopUpOptionFormValues[] = [];
    const optionForUpdate: ExternalAccountTopUpOptionFormValues[] = [];
    const optionForWithErrors: ExternalAccountTopUpOptionFormValues[] = [];

    topUpOptions?.forEach((item) => {
      if (!item?.paymentType || !item?.currencyId) {
        optionForWithErrors.push(item);
      }

      if (item?.id?.includes('option')) {
        optionsForCreate.push(item);
      }

      if (!item?.id?.includes('option')) {
        const currentDefaultOption = defaultTopUpOptions?.find(
          (defaultOption) => defaultOption?.id === item?.id,
        );

        if (currentDefaultOption && !isEqual(currentDefaultOption, item)) {
          optionForUpdate.push(item);
        }
      }
    });

    if (!optionForWithErrors?.length) {
      if (prevStatus !== status) {
        const response = (await updateStatus({
          ids: [accountId as string],
          active: Boolean(status === 'enabled'),
        })) as { error?: ResponseError };

        if (response?.error) {
          responseErrorsResolver({
            errors: response?.error,
            setError: () => {},
            setGlobalError,
          });
        }
      }

      if (!isEqual(externalAccount?.currenciesIds, selectedCurrencies)) {
        const response = (await updateCustomerExternalAccountCurrencies({
          id: externalAccount?.id as string,
          currenciesIds: selectedCurrencies,
        })) as { error?: ResponseError };

        if (response?.error) {
          responseErrorsResolver({
            errors: response?.error,
            setError: () => {},
            setGlobalError,
          });
        }
      }

      if (optionsForCreate?.length) {
        await Promise.all(
          optionsForCreate?.map(async (item) => {
            const body: ExternalAccountTopUpOptionFormValues = {
              paymentType: item?.paymentType,
              currencyId: item?.currencyId,
              bankAccountHolderName: item?.bankAccountHolderName,
              accountNumber: item?.accountNumber,
              bankAddress: item?.bankAddress,
              bic: item?.bic,
              iban: item?.iban,
              bankName: item?.bankName,
              pspIntegrationAccountId: externalAccount?.id,
            };

            if (item?.thirdPartyReference) {
              body.thirdPartyReference = item.thirdPartyReference;
            }

            if (
              item?.routingCodeEntries?.filter(
                (code) => code?.routingCodeKey && code?.routingCodeValue,
              )?.length
            ) {
              body.routingCodeEntries = item?.routingCodeEntries
                ?.filter(
                  (code) => code?.routingCodeKey && code?.routingCodeValue,
                )
                ?.map((code) => ({
                  routingCodeKey: code.routingCodeKey,
                  routingCodeValue: code.routingCodeValue,
                }));
            }

            await createPaymentAccounts(body);
          }),
        );
      }

      if (optionForUpdate?.length) {
        await Promise.all(
          optionForUpdate?.map(async (item) => {
            const body: ExternalAccountTopUpOptionFormValues = {
              paymentType: item?.paymentType,
              currencyId: item?.currencyId,
              bankAccountHolderName: item?.bankAccountHolderName,
              accountNumber: item?.accountNumber,
              bankAddress: item?.bankAddress,
              bic: item?.bic,
              iban: item?.iban,
              bankName: item?.bankName,
            };

            if (item?.thirdPartyReference) {
              body.thirdPartyReference = item.thirdPartyReference;
            }

            if (
              item?.routingCodeEntries?.filter(
                (code) => code.routingCodeKey && code.routingCodeValue,
              )?.length
            ) {
              body.routingCodeEntries = item?.routingCodeEntries
                ?.filter((code) => code.routingCodeKey && code.routingCodeValue)
                ?.map((code) => ({
                  routingCodeKey: code.routingCodeKey,
                  routingCodeValue: code.routingCodeValue,
                }));
            }

            const currentDefaultTopUpOptions = defaultTopUpOptions?.find(
              (option) => option?.id === item?.id,
            );

            if (
              !item?.routingCodeEntries?.filter(
                (code) => code.routingCodeKey && code.routingCodeValue,
              ) &&
              currentDefaultTopUpOptions &&
              currentDefaultTopUpOptions?.routingCodeEntries?.length
            ) {
              body.routingCodeEntries = null;
            }

            await updatePaymentAccounts({
              id: item?.id as string,
              body,
            });
          }),
        );
      }
    } else {
      const optionErrors: {
        id: string;
        currencyId?: string;
        paymentType?: string;
      }[] = [];

      optionForWithErrors?.forEach((item) => {
        const body: {
          id: string;
          currencyId?: string;
          paymentType?: string;
        } = {
          id: item.id as string,
        };

        if (!item?.currencyId) {
          body.currencyId = 'Currency is required';
        }

        if (!item.paymentType) {
          body.paymentType = 'Payment Type is required';
        }

        optionErrors.push(body);
      });

      setOptionsErrors(optionErrors);
    }
  };

  const onSearch = (value: string) => {
    const nextCurrencies: Currency[] = value
      ? availableCurrencies?.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        )
      : availableCurrencies?.map((item) => item);

    setSearchValue(value);

    setCurrencies(nextCurrencies);
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PSP_INTEGRATION_ACCOUNT&entityId=${accountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [accountId],
  );

  useEffect(() => {
    if (deletePaymentAccountsIsSuccess) {
      setDeleteConfirmationVisible(false);
      setSelectedOptionId('');
    }
  }, [deletePaymentAccountsIsSuccess]);

  useEffect(() => {
    if (externalAccountTopUpOptions) {
      const nextTopUpOptions = externalAccountTopUpOptions?.map((item) => ({
        id: item?.id,
        paymentType: item?.paymentType,
        currencyId: item?.currencyId,
        bankAccountHolderName: item?.bankAccountHolderName,
        accountNumber: item?.accountNumber,
        bankAddress: item?.bankAddress,
        bic: item?.bic,
        iban: item?.iban,
        bankName: item?.bankName,
        thirdPartyReference: item?.thirdPartyReference,
        pspIntegrationAccountId: item?.pspIntegrationAccountId,
        routingCodeEntries:
          item?.routingCodeEntries?.map((code) => ({
            id: uuid(),
            routingCodeKey: code?.routingCodeKey,
            routingCodeValue: code?.routingCodeValue,
          })) || [],
      }));

      setDefaultTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
      setTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
    }
  }, [externalAccountTopUpOptions]);

  useEffect(() => {
    if (
      activeTab === 'top-up' &&
      accountId &&
      externalAccount?.pspIntegrationPayment?.paymentsTypes?.length &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
      )
    ) {
      getExternalAccountTopUpOptions({
        id: accountId,
        // query: `&paymentType=${externalAccount?.pspIntegrationPayment?.paymentsTypes?.join('&paymentType=')}`,
      });
    }
  }, [
    activeTab,
    accountId,
    externalAccount?.pspIntegrationPayment?.paymentsTypes,
  ]);

  useEffect(() => {
    if (
      activeTab === 'change-logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');
    let link = '';

    if (type === CustomerType.INDIVIDUAL) {
      link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST, {
        customerId: (customerId as string) || '',
      });
    } else {
      link = generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST, {
        customerId: (customerId as string) || '',
      });
    }

    dispatch(clearExternalAccountPanel());

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (accountId) {
      getExternalAccount(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (externalAccount) {
      getAccountsBalancesAction(externalAccount?.account?.id);
      setSelectedCurrencies(externalAccount?.currenciesIds || []);
    }
  }, [externalAccount]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (externalAccount) {
      setStatus(externalAccount.active ? 'enabled' : 'disabled');

      if (
        paymentTypes &&
        balances &&
        dictionariesCurrencies &&
        pspIntegrations
      ) {
        const types: string[] = [];
        const nextCurrenciesList: Currency[] = [];

        externalAccount?.pspIntegrationPayment?.paymentsTypes?.forEach(
          (item) => {
            if (
              paymentTypes?.find((typeValue) => typeValue.paymentType === item)
            ) {
              types.push(
                paymentTypes?.find(
                  (typeValue) => typeValue.paymentType === item,
                )?.name as string,
              );
            }
          },
        );

        const currentPspIntegration = pspIntegrations?.find(
          (item) => item.id === externalAccount.pspPartner?.pspIntegrationId,
        );

        if (currentPspIntegration) {
          if (
            currentPspIntegration.name !== 'OPEN_PAYD' &&
            currentPspIntegration.name !== 'Keel'
          ) {
            const currentPaymentType = paymentTypes?.find(
              (item) =>
                item.paymentType ===
                externalAccount?.pspIntegrationPayment?.paymentsTypes[0],
            );

            if (currentPaymentType) {
              if (
                currentPaymentType?.paymentType !==
                  PaymentType.CRYPTO_ASSET_PAYMENT ||
                (currentPaymentType?.paymentType ===
                  PaymentType.CRYPTO_ASSET_PAYMENT &&
                  !externalAccount.caspId)
              ) {
                currentPaymentType?.currencies?.edges?.forEach((item) => {
                  if (
                    balances?.find(
                      (balance) => balance.currencyId === item.node.id,
                    )
                  ) {
                    nextCurrenciesList.push(item.node);
                  }
                });
              } else if (
                currentPaymentType.paymentType ===
                  PaymentType.CRYPTO_ASSET_PAYMENT &&
                externalAccount.caspId &&
                casp
              ) {
                const currentCasp = casp?.find(
                  (item) => item.id === externalAccount.caspId,
                );

                if (currentCasp) {
                  const caspCurrencies = currentCasp?.currencies?.map(
                    (item) => item.currency,
                  );

                  if (caspCurrencies?.length) {
                    caspCurrencies?.forEach((item) => {
                      if (
                        balances?.find(
                          (balance) => balance.currencyId === item.id,
                        )
                      ) {
                        nextCurrenciesList.push(item);
                      }
                    });

                    setCurrencies(nextCurrenciesList);
                    setAvailableCurrencies(nextCurrenciesList);
                  }
                }
              }
            }
          }

          if (
            currentPspIntegration.name === 'OPEN_PAYD' ||
            currentPspIntegration.name === 'Keel'
          ) {
            if (currentPspIntegration.name === 'OPEN_PAYD') {
              setIsOpenPayd(true);
            }

            if (currentPspIntegration.name === 'Keel') {
              setIsKeel(true);
            }
            const integrationCurrencies: { name: string; id: string }[] = [];

            currentPspIntegration?.pspIntegrationPayments?.forEach((item) => {
              item.currencies?.forEach((currencyItem) => {
                if (
                  !integrationCurrencies?.find(
                    (integrationCurrency) =>
                      integrationCurrency.id === currencyItem.id,
                  )
                ) {
                  integrationCurrencies.push(currencyItem.currency);
                }
              });
            });

            if (integrationCurrencies?.length) {
              integrationCurrencies?.forEach((item) => {
                if (
                  balances?.find((balance) => balance.currencyId === item.id)
                ) {
                  nextCurrenciesList.push(item as Currency);
                }
              });
            }
          }
        }

        if (nextCurrenciesList?.length) {
          setCurrencies(nextCurrenciesList);
          setAvailableCurrencies(nextCurrenciesList);
        }

        setAvailablePaymentTypes(types);
      }
    }
  }, [
    externalAccount,
    paymentTypes,
    balances,
    dictionariesCurrencies,
    pspIntegrations,
  ]);

  useEffect(() => {
    if (
      (updateCustomerExternalAccountCurrenciesIsSuccess ||
        createPaymentAccountsIsSuccess ||
        updatePaymentAccountsIsSuccess) &&
      !createPaymentAccountsInProgress &&
      !updatePaymentAccountsInProgress
    ) {
      handleClose();
    }
  }, [
    updateCustomerExternalAccountCurrenciesIsSuccess,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    createPaymentAccountsIsSuccess,
    updatePaymentAccountsIsSuccess,
  ]);

  let content;

  if (externalAccountIsLoading || externalAccountIsFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        {(Boolean(
          externalAccount?.pspIntegrationPaymentAccountsCount &&
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
          ),
        ) ||
          activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) && (
          <Tabs
            tabs={[
              {
                name: 'Details',
                value: 'details',
              },
              {
                name: 'Top-up options',
                value: 'top-up',
                hidden:
                  Boolean(
                    externalAccount?.createType === 'AUTO' &&
                    !externalAccount?.pspIntegrationPaymentAccountsCount,
                  ) ||
                  !activePermissions?.includes(
                    PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
                  ),
              },
              {
                name: 'Change Log',
                value: 'change-logs',
                hidden: !activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                ),
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) =>
              setActiveTab(tab as 'details' | 'top-up' | 'change-logs')
            }
            wrapperClassName={cn.externalAccountTabs}
          />
        )}
        <Activity mode={activeTab === 'details' ? 'visible' : 'hidden'}>
          <ExternalAccountForm
            externalAccount={externalAccount}
            status={status}
            setStatus={setStatus}
            availablePaymentTypes={availablePaymentTypes}
            searchValue={searchValue}
            onSearch={onSearch}
            currencies={currencies}
            activePermissions={activePermissions}
            organizationStatus={organizationStatus}
            casp={casp}
            balancesIsLoading={balancesIsLoading || balancesIsFetching}
            selectedCurrencies={selectedCurrencies}
            setSelectedCurrencies={setSelectedCurrencies}
            isOpenPayd={isOpenPayd}
            isKeel={isKeel}
          />
        </Activity>
        <Activity mode={activeTab === 'top-up' ? 'visible' : 'hidden'}>
          <>
            {(topUpIsLoading || topUpIsFetching) && <SidePanelLoader />}
            {!topUpIsLoading && !topUpIsFetching && (
              <>
                <div className={cn.topUpOptionsTitleWrapper}>
                  <div className={cn.topUpOptionsTitleWrapperLeft}>
                    <div className={cn.topUpOptionsTitle}>Top-up options</div>
                    <div className={cn.topUpOptionsDescription}>
                      Payment details the customer uses to top up this account
                    </div>
                  </div>
                  {externalAccount?.createType === 'MANUAL' &&
                    activePermissions?.includes(
                      PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_ADD_ACTION,
                    ) && (
                      <ButtonSecondary
                        className={`${cn.createOptionButton} button-white`}
                        icon={<IconExpand />}
                        text="Add option"
                        size={ButtonSize.SMALL}
                        onClick={() => {
                          const nextOptions = (
                            [] as ExternalAccountTopUpOptionFormValues[]
                          ).concat(topUpOptions);

                          nextOptions.unshift({
                            id: `option-${uuid()}`,
                            paymentType: '',
                            currencyId: '',
                            bankAccountHolderName: '',
                            accountNumber: '',
                            bankAddress: '',
                            bic: '',
                            iban: '',
                            thirdPartyReference: '',
                            bankName: '',
                            routingCodeEntries: [],
                          });

                          setTopUpOptions(nextOptions);
                        }}
                      />
                    )}
                </div>
                {!topUpOptions?.length && (
                  <div className={cn.emptyTopUpOption}>
                    No top up options yet
                  </div>
                )}
                {topUpOptions &&
                  topUpOptions?.length > 0 &&
                  topUpOptions?.map((item) => (
                    <ExternalAccountTopUpOptionItem
                      key={item.id}
                      topUpOption={item}
                      paymentTypes={paymentTypes}
                      currencies={currenciesList}
                      setSelectedOptionId={setSelectedOptionId}
                      setDeleteConfirmationVisible={
                        setDeleteConfirmationVisible
                      }
                      activePermissions={activePermissions}
                      isEditable={
                        externalAccount?.createType === 'MANUAL' &&
                        activePermissions?.includes(
                          PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_EDIT_ACTION,
                        )
                      }
                      newOption={Boolean(item?.id?.includes('option'))}
                      setTopUpOptions={setTopUpOptions}
                      topUpOptions={topUpOptions}
                      optionsError={optionsErrors?.find(
                        (error) => error?.id === item.id,
                      )}
                      optionsErrors={optionsErrors}
                      setOptionsErrors={setOptionsErrors}
                    />
                  ))}
              </>
            )}
          </>
        </Activity>
        <Activity mode={activeTab === 'change-logs' ? 'visible' : 'hidden'}>
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="PSP_INTEGRATION_ACCOUNT"
                entityId={accountId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        </Activity>
      </>
    );
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        errorSidepanel={globalError}
        title="External Account settings"
        sidepanelExpandedKey="customer-create-external-account"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.PSP_INTEGRATION_ACCOUNT_EDIT_ACTION,
            ) &&
              organizationStatus !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  text={
                    requestInProgress ||
                    updateCustomerExternalAccountCurrenciesInProgress ||
                    createPaymentAccountsInProgress ||
                    updatePaymentAccountsInProgress
                      ? 'Loading...'
                      : 'Save'
                  }
                  disabled={
                    !isDirty ||
                    requestInProgress ||
                    updateCustomerExternalAccountCurrenciesInProgress ||
                    createPaymentAccountsInProgress ||
                    updatePaymentAccountsInProgress
                  }
                  onClick={onSubmit}
                  bold
                />
              )}

            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Delete this option?"
        description=""
        onCancelClick={() => {
          setDeleteConfirmationVisible(false);
          setSelectedOptionId('');
        }}
        onOkClick={deletePaymentAccountsAction}
        cancelButtonText="Cancel"
        okButtonText={
          deletePaymentAccountsInProgress ? 'Loading..' : 'Yes, Delete'
        }
        okButtonDisabled={deletePaymentAccountsInProgress}
        isVisible={deleteConfirmationVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ExternalAccount;
